import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api, cardProduct, fetchAllProducts, hasRealImage, offerPct, pickBrandDiverse } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

const OFFER_GROUPS = [
  { key: "50", min: 50, label: "50% offers" },
  { key: "30", min: 30, label: "30% offers" },
  { key: "20", min: 20, label: "20% offers" },
  { key: "10", min: 10, label: "10% offers" },
];

export default function OfferEdits({ data }) {
  const eyebrow = data?.eyebrow || "Live Offers";
  const [activeKey, setActiveKey] = useState("30");
  const userPicked = useRef(false);
  const [pool, setPool] = useState(null);
  const railRef = useRef(null);
  const { catalog, pincodeInfo } = useStore();
  const storeId = pincodeInfo?.store_id || "";
  const group = OFFER_GROUPS.find((g) => g.key === activeKey);

  const brandMap = useMemo(
    () => Object.fromEntries(catalog.brands.map((b) => [b.id, b.name])),
    [catalog.brands]
  );

  useEffect(() => {
    let cancelled = false;
    fetchAllProducts({ ...(storeId && { store_id: storeId }) }, (partial) => { if (!cancelled) setPool(partial); })
      .then((all) => { if (!cancelled) setPool(all); })
      .catch(() => { if (!cancelled) setPool([]); });
    return () => { cancelled = true; };
  }, [storeId]);

  const countFor = (min) =>
    (pool || []).filter((p) => hasRealImage(p) && offerPct(p) >= min && (p.in_stock ?? true)).length;

  // Default tab: highest offer group with more than 5 products (50% only when it has enough), else 30%
  useEffect(() => {
    if (!pool || userPicked.current) return;
    if (countFor(50) > 5) setActiveKey("50");
    else setActiveKey("30");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool]);

  const loading = pool === null;
  const items = useMemo(
    () =>
      pickBrandDiverse(
        (pool || [])
          .filter((p) => hasRealImage(p) && offerPct(p) >= group.min && (p.in_stock ?? true))
          .sort((a, b) => offerPct(b) - offerPct(a)),
        10
      ),
    [pool, group.min]
  );

  const scroll = (dir) => railRef.current?.scrollBy({ left: dir * 560, behavior: "smooth" });

  return (
    <section id="offers" data-testid="offer-edits-section" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">{eyebrow}</p>
            <h2 className="mt-3 max-w-xl font-display text-3xl leading-tight tracking-tight text-cream sm:text-5xl">
              {data?.heading || (<>Deals at every <span className="italic text-gold">offer level</span></>)}
            </h2>
          </div>
          <div className="flex gap-2">
            <button data-testid="offer-rail-prev-button" onClick={() => scroll(-1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/50 text-cream transition-colors hover:border-gold hover:text-gold" aria-label="Scroll offers left">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button data-testid="offer-rail-next-button" onClick={() => scroll(1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/50 text-cream transition-colors hover:border-gold hover:text-gold" aria-label="Scroll offers right">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-6 flex gap-2 overflow-x-auto pb-2 no-scrollbar" role="tablist" aria-label="Offer groups">
          {OFFER_GROUPS.map((g) => (
            <button
              key={g.key}
              data-testid={`offer-tab-${g.key}`}
              role="tab"
              aria-selected={activeKey === g.key}
              onClick={() => { userPicked.current = true; setActiveKey(g.key); }}
              className={`relative shrink-0 rounded-full px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${activeKey === g.key ? "text-white" : "text-sage hover:text-cream"}`}
            >
              {activeKey === g.key && (
                <motion.span layoutId="offer-tab-pill" className="absolute inset-0 rounded-full bg-black" transition={{ type: "spring", stiffness: 300, damping: 28 }} />
              )}
              <span className="relative z-10">{g.label}</span>
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeKey}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          ref={railRef}
          data-testid={`offer-rail-${activeKey}`}
          className="mt-5 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 no-scrollbar"
        >
          {loading ? (
            [...Array(5)].map((_, i) => <div key={i} className="aspect-[4/5] w-[220px] shrink-0 animate-pulse rounded-2xl bg-moss sm:w-[250px]" />)
          ) : items.length === 0 ? (
            <div className="flex w-full flex-col items-center rounded-3xl border border-dashed border-champagne/60 py-16 text-center" data-testid="offer-rail-empty">
              <p className="font-display text-2xl text-cream">No {group.label} live right now</p>
              <p className="mt-2 max-w-sm text-sm text-sage">New offers are switched on by HQ all the time — check the smaller offer groups meanwhile.</p>
              <Link to="/shop" className="mt-5 flex items-center gap-2 rounded-full border border-gold/50 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-forest">
                Browse full shelf <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ) : (
            items.map((p) => <ProductCard key={p.id} p={cardProduct(p, brandMap[p.brand_id])} />)
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-2 flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fern" data-testid="offer-group-count">
          {loading ? "Loading…" : `${items.length} product${items.length === 1 ? "" : "s"} at ${group.min}% & above`} — live from the Soonyoung ERP
        </p>
        <Link
          data-testid="offer-group-view-all"
          to={`/offers?min=${group.min}`}
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold transition-colors hover:text-cream"
        >
          View all <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}
